import { useImporterTheme } from '@shared/components/csv-importer/useImporterTheme';
import { FC } from 'react';
import { ReactSpreadsheetImport } from 'react-spreadsheet-import';
import { Result } from 'react-spreadsheet-import/types/types';

export type ProductImportRow = {
  productName: string;
};

interface CsvImporterProductsProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (productNames: string[]) => Promise<void> | void;
}

const fields = [
  {
    label: 'Product name',
    key: 'productName' as const,
    alternateMatches: ['name', 'product', 'recipe', 'recipe name', 'item'],
    fieldType: { type: 'input' as const },
    validations: [
      {
        rule: 'required' as const,
        errorMessage: 'Product name is required.',
        level: 'error' as const,
      },
      {
        rule: 'unique' as const,
        errorMessage: 'Product name is duplicated within the file.',
        level: 'warning' as const,
      },
      {
        // Mirror the backend's CharField(max_length=128) so users see the
        // problem at the validate step rather than as a generic 400 on submit.
        rule: 'regex' as const,
        value: '^.{1,128}$',
        errorMessage: 'Product name must be 128 characters or fewer.',
        level: 'error' as const,
      },
    ],
    example: 'Chicken Caesar Salad',
  },
];

const CsvImporterProducts: FC<CsvImporterProductsProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const theme = useImporterTheme();

  const handleSubmit = async (data: Result<keyof ProductImportRow>) => {
    const productNames = data.validData
      .map((row) => row.productName?.toString().trim())
      .filter((name): name is string => Boolean(name));
    await onSubmit(productNames);
  };

  const translations = {
    uploadStep: {
      title: 'Upload a spreadsheet with your product names',
      // Blank the manifest sub-heading — the step title above now carries this
      // message, so the library's default ("Data that we expect:") would just
      // duplicate it.
      manifestTitle: '',
      manifestDescription: (
        <div className="space-y-3 py-4">
          <p>
            Each row should be one product or recipe you want to create, such as
            a menu item or a SKU. We&rsquo;ll use those names to auto-generate
            an ingredient list that you can review.
          </p>
          <p>
            If a name already matches an existing product in your account,
            we&rsquo;ll keep that existing product as-is and just add it to the
            collection. No duplicates will be created.
          </p>
        </div>
      ),
    },
  };

  return (
    <ReactSpreadsheetImport<keyof ProductImportRow>
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      fields={fields}
      autoMapSelectValues
      allowInvalidSubmit={false}
      customTheme={theme}
      translations={translations}
      isNavigationEnabled
      uploadStepHook={async (data) => {
        // `data` here is the raw parsed sheet including the header row, so
        // 1,001 corresponds to 1 header + 1,000 data rows. Mirrors how
        // food-import sets its own ceiling (100,001 = header + 100,000).
        if (data.length > 1001) {
          onClose();
          throw new Error(
            'Limit exceeded: maximum of 1,000 products per import.'
          );
        }
        return data;
      }}
    />
  );
};

export default CsvImporterProducts;
